import React from 'react';
import { PropTypes } from 'prop-types';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  ResourceRequestsToRescheduleByStatus2,
  ResourceRequestsToRescheduleByStatus
} from '~/types';
import { isNullOrUndefined } from '~/modules/common/util';
import { useForm } from './useForm';
import {
  useResourceRequestsStatusCountSummary,
  useTasksStatusCountSummary,
  useBillingItemsCount,
  useCompletedResourceAllocationsSummary
} from './hooks';
import ReschedulingProjectDialogContentData from './ReschedulingProjectDialogContentData';
import RescheduleProjectDialogContentData from './RescheduleProjectDialogContentData';
import RescheduleProjectSkeleton from './RescheduleProjectSkeleton';

export const RescheduleProjectDialogContent = ({
  project,
  onClose,
  newStartDate
}) => {
  const {
    hasEditProjectBillingContracts,
    featureFlags: { isPsaRmpTaskAllocation1Enabled }
  } = useMeContext();
  const {
    id: projectId,
    billingDetails,
    isTaskDateRollupEnabled: projectIsTaskDateRollupEnabled,
    permittedActionUris
  } = project;

  const canEditTasks = permittedActionUris.includes(
    'urn:replicon:project-action:edit-tasks'
  );

  const canEditBillingContracts = permittedActionUris.includes(
    'urn:replicon:project-action:edit-billing-contracts'
  );

  const canViewBillingData = permittedActionUris.includes(
    'urn:replicon:project-action:view-billing-data'
  );

  const {
    loading: isRequestsStatusCountSummaryLoading,
    resourceRequestsStatusCountSummary
  } = useResourceRequestsStatusCountSummary({ projectId });
  const {
    data: { tasksStatusCountSummary } = {},
    loading: tasksStatusCountSummaryLoading
  } = useTasksStatusCountSummary(projectId);

  const shouldFetchTaskDateRollup = isNullOrUndefined(
    projectIsTaskDateRollupEnabled
  );

  const {
    loading: resourceAllocationsSummaryLoading,
    projectResourceAllocationsSummary,
    isTaskDateRollupEnabled: fetchedIsTaskDateRollupEnabled
  } = useCompletedResourceAllocationsSummary({
    projectId,
    skip: !isPsaRmpTaskAllocation1Enabled && !shouldFetchTaskDateRollup,
    includeAllocations: isPsaRmpTaskAllocation1Enabled,
    includeTaskRollup: shouldFetchTaskDateRollup
  });

  const scheduleAllTasks = shouldFetchTaskDateRollup
    ? fetchedIsTaskDateRollupEnabled
    : projectIsTaskDateRollupEnabled;

  const { completed, proposed, total } = resourceRequestsStatusCountSummary;

  const canRescheduleResourceRequests = isPsaRmpTaskAllocation1Enabled
    ? Boolean(total - completed)
    : total > 0;

  const canRescheduleResourceAllocations =
    projectResourceAllocationsSummary?.resourceAllocationCount > 0;

  const {
    data: { billingItemsCount = 0 } = {},
    loading: isBillingItemsCountLoading
  } = useBillingItemsCount({
    projectId,
    skip: !hasEditProjectBillingContracts
  });

  const canRescheduleTasks = canEditTasks
    ? Boolean(
        tasksStatusCountSummary &&
          (tasksStatusCountSummary.notStartedTasksCount > 0 ||
            tasksStatusCountSummary.inProgressTasksCount > 0 ||
            tasksStatusCountSummary.completedTasksCount > 0)
      )
    : false;

  const canRescheduleBillPlan =
    canEditBillingContracts &&
    canViewBillingData &&
    billingDetails?.billingTypeUri !==
      'urn:replicon:billing-type:time-and-material'
      ? Boolean(billingItemsCount === 0)
      : false;

  const { form, jobState } = useForm({
    project,
    newStartDate,
    canRescheduleTasks,
    canRescheduleBillPlan,
    resourceRequestsToReschedule: isPsaRmpTaskAllocation1Enabled
      ? ResourceRequestsToRescheduleByStatus2.AllResourceRequests
      : proposed > 0 || completed > 0
      ? ResourceRequestsToRescheduleByStatus.WithoutProposedOrAcceptedResourcesRequests
      : null,
    canRescheduleResourceRequests,
    canRescheduleResourceAllocations,
    isPsaRmpTaskAllocation1Enabled,
    scheduleAllTasks
  });

  const isLoading =
    tasksStatusCountSummaryLoading ||
    isRequestsStatusCountSummaryLoading ||
    isBillingItemsCountLoading ||
    resourceAllocationsSummaryLoading;

  const { jobId, jobInProgress } = jobState;

  return (
    <>
      {jobInProgress ? (
        <ReschedulingProjectDialogContentData jobId={jobId} onClose={onClose} />
      ) : isLoading ? (
        <RescheduleProjectSkeleton />
      ) : (
        <RescheduleProjectDialogContentData
          form={form}
          project={project}
          onClose={onClose}
          canRescheduleBillPlan={canRescheduleBillPlan}
          canRescheduleTasks={canRescheduleTasks}
          canRescheduleResourceRequests={canRescheduleResourceRequests}
          canRescheduleResourceAllocations={canRescheduleResourceAllocations}
          tasksStatusCountSummary={tasksStatusCountSummary}
          resourceRequestsStatusCountSummary={
            resourceRequestsStatusCountSummary
          }
          scheduleAllTasks={scheduleAllTasks}
          projectResourceAllocationsSummary={projectResourceAllocationsSummary}
        />
      )}
    </>
  );
};

RescheduleProjectDialogContent.propTypes = {
  project: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  newStartDate: PropTypes.object
};

export default RescheduleProjectDialogContent;
